import React, { useEffect } from "react";
import { ArrowLeft, Download, GraduationCap, Shield, Award, Users, CheckCircle, Phone, ChevronRight, BookOpen } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";

const trainingCategories = [
  {
    title: "Hubarbeitsbühnenschulung",
    description: "Umfassende Schulung für den sicheren Umgang mit Hubarbeitsbühnen aller Art. Theorie und Praxis nach IPAF-Standards.",
    icon: "ArrowUp",
    highlights: ["IPAF-zertifiziert", "Theorie & Praxis", "PAL Card"],
  },
  {
    title: "Gabelstaplerschulung",
    description: "Professionelle Ausbildung zum sicheren Führen von Gabelstaplern gemäß DGUV Grundsatz 308-001 und DGUV Vorschrift 68.",
    icon: "Forklift",
    highlights: ["DGUV-konform", "Alle Staplertypen", "Fahrausweis"],
  },
  {
    title: "Teleskopstaplerschulung",
    description: "Spezialisierte Schulung für Teleskopstapler mit allen vorgeschriebenen theoretischen und praktischen Inhalten.",
    icon: "Telescope",
    highlights: ["Praxisnah", "Alle Modelle", "Zertifikat"],
  },
  {
    title: "Kran- und Anschlägerschulung",
    description: "Fachgerechte Ausbildung für Kranführer und Anschläger. Sicherer Umgang mit Lastaufnahmemitteln und Hebetechnik.",
    icon: "Crane",
    highlights: ["Kranführer", "Anschläger", "Lastaufnahme"],
  },
];

const staplerInhalte = {
  theorie: [
    "Rechtliche Grundlagen",
    "Unfallgeschehen",
    "Aufbau und Funktion von Flurförderzeugen und Anbaugeräten",
    "Antriebsarten",
    "Standsicherheit",
    "Betrieb allgemein",
    "Regelmäßige Prüfungen",
    "Umgang mit Lasten",
    "Sondereinsätze",
    "Verkehrsregeln / Verkehrswege",
  ],
  praxis: [
    "Einweisung am Flurförderzeug",
    "Fahrübungen nach DGUV Grundsatz 308-001 (alt BGG 925)",
    "Praktische Prüfung",
  ],
};

const FadeIn = ({ children, delay = 0, className = "" }) => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const Schulungen = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <div className="relative h-[50vh] min-h-[400px] overflow-hidden">
        <img
          src="/uploads/lafobwnm_Schulungen.jpeg"
          alt="Schulungen"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/10" />
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-[1200px] mx-auto px-6 pb-12 w-full">
            <button
              onClick={() => navigate("/")}
              className="flex items-center gap-2 text-sm text-white/70 hover:text-white mb-4 transition-colors"
            >
              <ArrowLeft size={16} />
              Zurück
            </button>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-4xl md:text-6xl font-bold text-white tracking-tight mb-3"
            >
              Sicherheitsschulungen
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-white/70 text-lg max-w-xl"
            >
              Praxisnahe Schulungen für den sicheren Umgang mit Arbeitsbühnen,
              Staplern und Kranen – IPAF-zertifiziert und bundesweit verfügbar.
            </motion.p>
          </div>
        </div>
      </div>

      {/* Catalog Download Banner */}
      <div className="bg-[#f5f5f7] border-b border-[#e5e5e5]">
        <div className="max-w-[1200px] mx-auto px-6 py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-[#4a7c59]/10 flex items-center justify-center flex-shrink-0">
                <BookOpen size={22} className="text-[#4a7c59]" />
              </div>
              <div>
                <p className="text-base font-semibold text-[#1d1d1f]">
                  Schulungskatalog zum Download
                </p>
                <p className="text-sm text-[#6e6e73]">
                  Unser komplettes Schulungsangebot als PDF
                </p>
              </div>
            </div>
            <a
              href="/datenblaetter/Schulungskatalog.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-[#4a7c59] hover:bg-[#3d6a4a] text-white text-sm font-medium px-6 py-3 rounded-xl transition-all duration-300 hover:shadow-lg"
            >
              <Download size={16} />
              Katalog herunterladen
            </a>
          </div>
        </div>
      </div>

      {/* Training Categories */}
      <section className="py-20">
        <div className="max-w-[1200px] mx-auto px-6">
          <FadeIn>
            <p className="text-sm font-medium text-[#4a7c59] tracking-widest uppercase mb-3 text-center">
              Unser Angebot
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1d1d1f] tracking-tight mb-5 text-center">
              Schulungen für jeden Bereich.
            </h2>
            <p className="text-lg text-[#6e6e73] max-w-2xl mx-auto text-center mb-14">
              Ob Hubarbeitsbühne, Gabelstapler, Teleskopstapler oder Kran – wir
              bieten Ihnen die passende Schulung.
            </p>
          </FadeIn>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {trainingCategories.map((cat, i) => (
              <FadeIn key={i} delay={i * 0.1}>
                <div className="group p-8 rounded-3xl bg-[#f5f5f7] hover:bg-white border border-transparent hover:border-[#e5e5e5] hover:shadow-xl hover:shadow-black/5 transition-all duration-500 h-full">
                  <div className="w-12 h-12 rounded-2xl bg-[#4a7c59]/10 flex items-center justify-center mb-5 group-hover:bg-[#4a7c59] group-hover:scale-110 transition-all duration-500">
                    <GraduationCap
                      size={22}
                      className="text-[#4a7c59] group-hover:text-white transition-colors duration-500"
                    />
                  </div>
                  <h3 className="text-xl font-semibold text-[#1d1d1f] tracking-tight mb-3">
                    {cat.title}
                  </h3>
                  <p className="text-sm text-[#6e6e73] leading-relaxed mb-5">
                    {cat.description}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {cat.highlights.map((h, j) => (
                      <span
                        key={j}
                        className="text-[11px] font-medium text-[#4a7c59] bg-[#4a7c59]/8 px-3 py-1.5 rounded-full"
                      >
                        {h}
                      </span>
                    ))}
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* IPAF Section */}
      <section className="py-20 bg-[#f5f5f7]">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <FadeIn>
              <div>
                <div className="flex items-center gap-4 mb-6">
                  <img
                    src="/uploads/ipaflogo.jpg"
                    alt="IPAF"
                    className="h-16 object-contain rounded-xl"
                  />
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-[#1d1d1f] tracking-tight mb-6">
                  IPAF-zertifizierte Schulungen
                </h2>
                <p className="text-[#6e6e73] leading-relaxed mb-5">
                  IPAF fördert den sicheren und effektiven Einsatz von
                  Höhenzugangstechnik weltweit. Als zertifiziertes
                  IPAF-Schulungszentrum führen wir professionelle Kurse durch,
                  die sowohl Theorie als auch Praxis umfassen – inklusive
                  schriftlicher und praktischer Prüfung.
                </p>
                <p className="text-[#6e6e73] leading-relaxed mb-6">
                  Die Kursteilnehmerzahl wird bewusst gering gehalten, um eine
                  optimale Betreuung zu gewährleisten. Kurse dauern in der Regel
                  ein bis zwei Tage. Das Kursmaterial ist in vielen Sprachen
                  erhältlich und wird regelmäßig aktualisiert.
                </p>
                <div className="flex flex-wrap gap-6 mb-8">
                  <div className="flex items-center gap-2">
                    <CheckCircle size={16} className="text-[#4a7c59]" />
                    <span className="text-sm text-[#1d1d1f]">TÜV-geprüft nach ISO 18878</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle size={16} className="text-[#4a7c59]" />
                    <span className="text-sm text-[#1d1d1f]">600+ Schulungszentren weltweit</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle size={16} className="text-[#4a7c59]" />
                    <span className="text-sm text-[#1d1d1f]">100.000+ Teilnehmer/Jahr</span>
                  </div>
                </div>

                <p className="text-[#1d1d1f] font-semibold mb-4">
                  Kursteilnehmer erhalten:
                </p>
                <div className="space-y-3 mb-6">
                  {[
                    "PAL Card (Powered Access Licence) – international anerkannt",
                    "Persönliches Sicherheitsleitfaden",
                    "Logbuch zur Dokumentation",
                    "Offizielles IPAF-Zertifikat",
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full bg-[#4a7c59]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <CheckCircle size={12} className="text-[#4a7c59]" />
                      </div>
                      <p className="text-sm text-[#1d1d1f]">{item}</p>
                    </div>
                  ))}
                </div>
                <div className="bg-white rounded-2xl p-5 border border-[#e5e5e5]">
                  <p className="text-sm text-[#6e6e73]">
                    <strong className="text-[#1d1d1f]">Über 100.000 Personen</strong> werden
                    jährlich durch das weltweite Netzwerk von über 600
                    IPAF-zertifizierten Schulungszentren geschult.
                  </p>
                </div>
              </div>
            </FadeIn>
            <FadeIn delay={0.2}>
              <div className="rounded-3xl overflow-hidden">
                <img
                  src="/uploads/w75tq66x_Ipaf-Schulung-1.jpg"
                  alt="IPAF Training"
                  className="w-full h-[400px] object-cover"
                  style={{ objectPosition: 'left bottom' }}
                />
              </div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="relative -mt-8 ml-6 inline-block bg-white rounded-2xl p-4 shadow-xl shadow-black/5 border border-[#e5e5e5]"
              >
                <Award size={24} className="text-[#4a7c59] mb-1" />
                <p className="text-xs font-semibold text-[#1d1d1f]">International</p>
                <p className="text-[10px] text-[#6e6e73]">anerkannt</p>
              </motion.div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* PAL Card / Operator Card */}
      <section className="py-20 bg-white">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <FadeIn>
              <div className="flex flex-col gap-6">
                <div className="rounded-3xl overflow-hidden">
                  <img
                    src="/uploads/sc37u0ej_Schulungsraum_1.jpeg"
                    alt="Schulungsraum Brinckmann"
                    className="w-full h-[320px] object-cover"
                  />
                </div>
                <div className="relative bg-[#f5f5f7] rounded-3xl p-10 flex items-center justify-center">
                  <img
                    src="/uploads/spawp8wq_Logo_OC-png.png"
                    alt="Operator Card"
                    className="w-56 h-auto object-contain"
                  />
                </div>
              </div>
            </FadeIn>
            <FadeIn delay={0.15}>
              <div>
                <p className="text-sm font-medium text-[#4a7c59] tracking-widest uppercase mb-3">
                  Ihr Nachweis
                </p>
                <h2 className="text-3xl md:text-4xl font-bold text-[#1d1d1f] tracking-tight mb-3">
                  Über die OperatorCARD
                </h2>
                <p className="text-xl font-semibold text-[#4a7c59] mb-6">
                  Aus der Praxis für die Praxis!
                </p>
                <p className="text-[#6e6e73] leading-relaxed mb-5">
                  Trainer mit jahrelanger Praxis-Erfahrung im Umgang mit mobilen
                  Arbeitsmitteln – das ist die Stärke der
                  OperatorCARD-Schulungszentren.
                </p>
                <p className="text-[#6e6e73] leading-relaxed mb-8">
                  Durch die tägliche Arbeit an und mit den Arbeitsmitteln sammeln
                  unsere Fachleute wertvolles Wissen und praktische Erfahrung.
                  Ziel von OperatorCARD-Schulungen ist dieses Know-How an die
                  Teilnehmer weiterzugeben. Dabei stellt die Schulung keine
                  Einbahnstraße dar. Nicht nur der Unterweisende bringt einen
                  Erfahrungsschatz mit, sondern auch die Teilnehmer. Wir freuen
                  uns auf euch!
                </p>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Flurförderzeuge / Staplerschulung */}
      <section className="py-20 bg-[#f5f5f7]">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <FadeIn>
              <div>
                <p className="text-sm font-medium text-[#4a7c59] tracking-widest uppercase mb-3">
                  Staplerschulung
                </p>
                <h2 className="text-3xl md:text-4xl font-bold text-[#1d1d1f] tracking-tight mb-6">
                  Flurförderzeuge
                </h2>
                <p className="text-[#6e6e73] leading-relaxed mb-6">
                  Wir bieten Ihnen Schulungen mit allen vorgeschriebenen
                  theoretischen und praktischen Lehrinhalten gemäß DGUV
                  Grundsatz 308-001 (bisher BGG 925) und DGUV Vorschrift 68
                  (bisher BGV D27) an.
                </p>
                <p className="text-[#6e6e73] leading-relaxed mb-6">
                  Im Rahmen dieser Unterweisung erlangen die Teilnehmer alle
                  notwendigen Kenntnisse zum Führen von Gabelstaplern und
                  Flurförderzeugen – von Aufbau und Funktion über unterschiedliche
                  Typen bis hin zu allen geltenden Sicherheitsbestimmungen.
                </p>
                <div className="bg-white rounded-2xl p-5 border border-[#e5e5e5] mb-6">
                  <div className="flex items-center gap-3 mb-2">
                    <Users size={18} className="text-[#4a7c59]" />
                    <p className="text-sm font-semibold text-[#1d1d1f]">
                      Flexible Schulungsorte
                    </p>
                  </div>
                  <p className="text-sm text-[#6e6e73]">
                    Wir schulen auf modernen Flurförderzeugen führender Hersteller
                    – entweder in unserem Schulungsraum oder auf Wunsch auch bei
                    Ihnen vor Ort.
                  </p>
                </div>
                <div className="rounded-3xl overflow-hidden">
                  <img
                    src="/uploads/m6ih74lk_Gabelstapler-1.jpg"
                    alt="Gabelstapler"
                    className="w-full h-80 object-cover"
                  />
                </div>
              </div>
            </FadeIn>

            <FadeIn delay={0.15}>
              <div className="bg-white rounded-3xl p-8 border border-[#e5e5e5] sticky top-24">
                <h3 className="text-xl font-bold text-[#1d1d1f] tracking-tight mb-6">
                  Inhalte der Staplerschulung
                </h3>

                {/* Theorie */}
                <div className="mb-6">
                  <p className="text-xs font-semibold text-[#4a7c59] uppercase tracking-wider mb-3">
                    Theoretische Ausbildung
                  </p>
                  <div className="space-y-2">
                    {staplerInhalte.theorie.map((item, i) => (
                      <div key={i} className="flex items-center gap-2.5">
                        <div className="w-1.5 h-1.5 rounded-full bg-[#4a7c59] flex-shrink-0" />
                        <p className="text-sm text-[#424245]">{item}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Praxis */}
                <div className="border-t border-[#e5e5e5] pt-6">
                  <p className="text-xs font-semibold text-[#4a7c59] uppercase tracking-wider mb-3">
                    Praktische Ausbildung
                  </p>
                  <div className="space-y-2">
                    {staplerInhalte.praxis.map((item, i) => (
                      <div key={i} className="flex items-center gap-2.5">
                        <div className="w-1.5 h-1.5 rounded-full bg-[#4a7c59] flex-shrink-0" />
                        <p className="text-sm text-[#424245]">{item}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Training Image */}
                <div className="mt-6 rounded-2xl overflow-hidden">
                  <img
                    src="/uploads/training.jpg"
                    alt="Schulung"
                    className="w-full h-48 object-cover"
                  />
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-white">
        <div className="max-w-[800px] mx-auto px-6">
          <FadeIn>
            <div className="bg-[#f5f5f7] rounded-3xl p-10 md:p-14">
              {/* Ansprechpartner */}
              <div className="flex flex-col items-center mb-8">
                <div className="w-24 h-24 rounded-full overflow-hidden ring-4 ring-white shadow-lg mb-4">
                  <img
                    src="/uploads/qhy8cd7i_Lars-Mengering-web.jpg"
                    alt="Lars Mengering"
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className="text-lg font-semibold text-[#1d1d1f]">Lars Mengering</h3>
                <p className="text-sm text-[#4a7c59] font-medium">Ihr Ansprechpartner für Schulungen</p>
              </div>

              <h2 className="text-3xl md:text-4xl font-bold text-[#1d1d1f] tracking-tight mb-4 text-center">
                Schulung anfragen
              </h2>
              <p className="text-[#6e6e73] max-w-lg mx-auto mb-8 leading-relaxed text-center">
                Lars Mengering berät Sie gerne persönlich zu unserem
                Schulungsangebot und findet den passenden Kurs für Sie und Ihre
                Mitarbeiter – individuell und flexibel.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <a
                  href="tel:+492871242660"
                  className="flex items-center justify-center gap-2 bg-[#4a7c59] hover:bg-[#3d6a4a] text-white text-sm font-medium px-8 py-3.5 rounded-xl transition-all duration-300 hover:shadow-lg"
                >
                  <Phone size={16} />
                  02871 / 242 660
                </a>
                <a
                  href="mailto:schulung@brinckmann-arbeitsbuehnen.de"
                  className="flex items-center justify-center gap-2 bg-white hover:bg-[#e5e5e5] border border-[#e5e5e5] text-[#1d1d1f] text-sm font-medium px-8 py-3.5 rounded-xl transition-all duration-300"
                >
                  E-Mail schreiben
                </a>
              </div>
              <p className="text-xs text-[#6e6e73] text-center mt-4">
                schulung@brinckmann-arbeitsbuehnen.de
              </p>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
};

export default Schulungen;
